import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, Chip, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormikContext } from 'formik';
import InputWrapper from '../../form/base/InputWrapper';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import { getTags } from '../../../services/flashbooks';
import { FlashDesignFormPayload } from '../../../types/flashbooks';
import {
  tagChipInputStyle,
  tagInputFieldStyle,
} from '../../../helpers/styles/flashbook';

interface Props {
  flashbookId: string | undefined;
}

export default function TagInputField({ flashbookId }: Props) {
  const { values, setFieldValue } = useFormikContext<FlashDesignFormPayload>();
  const [inputValue, setInputValue] = useState('');
  const debouncedQuery = useDebouncedValue(inputValue.trim(), 250);

  const { data: suggestions = [] } = useQuery({
    queryKey: [
      'flashbook-tags',
      { flashbookId, query: debouncedQuery, excludeTags: values.tags },
    ],
    enabled: !!flashbookId,
    queryFn: () =>
      getTags({
        flashbookId: flashbookId!,
        query: debouncedQuery || undefined,
        excludeTags: values.tags,
      }),
  });

  return (
    <InputWrapper name="tags" label="Tags (optional)">
      <Autocomplete
        multiple
        freeSolo
        filterSelectedOptions
        options={suggestions}
        value={values.tags}
        inputValue={inputValue}
        onInputChange={(_, next) => setInputValue(next)}
        onChange={(_, next) => {
          const cleaned = Array.from(
            new Set(next.map(t => t.trim()).filter((t): t is string => !!t)),
          );
          setFieldValue('tags', cleaned);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option}
                size="small"
                deleteIcon={<CloseIcon style={{ fontSize: 14 }} />}
                {...tagProps}
                sx={tagChipInputStyle}
              />
            );
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            placeholder="Add a tag…"
            sx={tagInputFieldStyle}
          />
        )}
      />
    </InputWrapper>
  );
}
